import * as React from "react";
import {
  Box,
  Tabs,
  Tab,
  Button,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { getApiDocsData } from "../../services/apiDocsService";
import { hasChildren } from "../../pages/dashboard/utils";
import { menu } from "./menu";
import DescriptionBox from "./DescriptionBox";
import APIServices from "./APIServices";
import DocumentIdentification from "./DocumentIdentification";
import CategoryOverview from "./CategoryOverview";
import { useLocation, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const drawerWidth = 280;

export const APIDocument = () => {
  const location = useLocation();
  const [data, setData] = React.useState(null);
  const [selectedDrawerItem, setSelectedDrawerItem] = React.useState(null);
  const [showBackButton, setShowBackButton] = React.useState(null);
  const [selectedCategory, setSelectedCategory] = React.useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = React.useState(null);
  const [showCategoryOverview, setShowCategoryOverview] = React.useState(false);
  const [selectedTabValue, setSelectedTabValue] = React.useState(null);

  const handleCategoryClick = (value: string) => {
    if (selectedCategory === value) {
      setSelectedCategory(null);
      setShowCategoryOverview(false);
      setSelectedDrawerItem(null);
      setSelectedTabValue(null);
    } else {
      setSelectedCategory(value);
      setShowCategoryOverview(true);
      setSelectedSubCategory(null);
      setSelectedDrawerItem(null);
      setSelectedTabValue(value);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  };

  const handleApiClickFromOverview = (api) => {
    setSelectedSubCategory(api.title);
    setShowCategoryOverview(false);
    setTimeout(() => {
      scrollToPosition(apiServicesRef);
    }, 300);
  };

  const createApiDocumentPdf = () => {
    const doc = new jsPDF();
    let currentY = 20;
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;
    const maxWidth = pageWidth - margin * 2;
    
    const greyColor = [30, 29, 28];
  
    const addTitleWithLine = (text, x, y, fontSize = 16, color = [0, 112, 192]) => {
      if (text) {
        doc.setFontSize(fontSize);
        doc.setTextColor(...color);
        doc.text(text, x, y);
        doc.setDrawColor(200);
        doc.line(x, y + 2, pageWidth - margin, y + 2);
        currentY = y + 12;
        doc.setTextColor(...greyColor);
      }
    };
  
    const addParagraphText = (text, x, y, fontSize = 12) => {
      if (text) {
        doc.setFontSize(fontSize);
        doc.setTextColor(...greyColor);
        const lines = doc.splitTextToSize(String(text), maxWidth);
        let newY = y;
        if (newY + lines.length * 8 > pageHeight - margin) {
          doc.addPage();
          newY = margin;
          currentY = margin;
        }
        doc.text(lines, x, newY);
        currentY = newY + lines.length * 8 + 2;
      }
    };
  
    addTitleWithLine(data?.Introduction.title, margin, currentY);
    addParagraphText(data.Introduction.description, margin, currentY + 1);
  
    addTitleWithLine(data.Authentication.title, margin, currentY + 2);
    addParagraphText(data.Authentication.description, margin, currentY + 1);
  
    addTitleWithLine(data?.httpStatusCode?.title, margin, currentY + 2);
    doc.autoTable({
      startY: currentY + 1,
      head: [['Code', 'Name', 'Explanation']],
      body: data.httpStatusCode.codeData.map(item => [
        item.code || '',
        item.name || '',
        item.explanation || ''
      ]),
      margin: { left: margin },
      headStyles: {
        fillColor: [0, 112, 192],   
        textColor: [255, 255, 255],
      },
    });
    currentY = doc.autoTable.previous.finalY + 10;
  
    addTitleWithLine(data.rateLimit.title, margin, currentY + 2);
    addParagraphText(data.rateLimit.description, margin, currentY + 1);
    currentY += 10;
  
    addTitleWithLine('API Services', margin, currentY);
  
    data?.apiServices?.forEach((service) => {
      addParagraphText(service.categoryName, margin, currentY, 16);
  
      service.apiDetails.forEach((apiDetail) => {
// Add background for apiDetail?.apiName
// Add full-line background for apiDetail?.apiName
if (apiDetail?.apiName) {
  const text = apiDetail.apiName;
  const fontSize = 14;
  const backgroundHeight = fontSize * 0.6; // Adjust for font size
  const backgroundY = currentY - backgroundHeight + 2; // Adjust Y position for padding
 
  // Draw full-width background rectangle
  doc.setFillColor(0, 112, 192); // Blue background color
  doc.rect(margin, backgroundY, pageWidth - margin * 2, backgroundHeight, 'F');
 
  // Add text over the background
  doc.setFontSize(fontSize);
  doc.setTextColor(255, 255, 255); // White text color
  doc.text(text, margin + 2, currentY);
 
  // Update currentY
  currentY += backgroundHeight + 6; // Add spacing after the section
  doc.setTextColor(...greyColor); // Reset text color for further text
}
 
// Add title with line
 
      
        addTitleWithLine("Description",  margin, currentY, 12)
        addParagraphText(`${apiDetail.APIDetail.description}`, margin, currentY);
 
        addTitleWithLine("Endpoint: ", margin, currentY , 12);
        addParagraphText(`${apiDetail.endpoint}`, margin, currentY);
 
        addTitleWithLine("Method: ", margin, currentY , 12);
        addParagraphText(`${apiDetail.apiMethod}`, margin, currentY);
 
        currentY += 2;
      
        if (apiDetail.header) {
          addTitleWithLine("Headers:", margin, currentY, 12);
          doc.autoTable({
            startY: currentY - 5,
            head: [['Name', 'Explanation']],
            body: apiDetail.header.codeData.map(item => [
              item.name || '',
              item.explanation || ''
            ]),
            margin: { left: margin },
            headStyles: {
              fillColor: [0, 112, 192],   
              textColor: [255, 255, 255],
            },
          });
          currentY = doc.autoTable.previous.finalY + 7;
        }
      
        if (apiDetail.requestParameter) {
          addTitleWithLine("Request Parameters:", margin, currentY, 12);
          doc.autoTable({
            startY: currentY - 5,
            head: [['Name', 'Explanation']],
            body: apiDetail.requestParameter.codeData.map(item => [
              item.name || '',
              item.explanation || ''
            ]),
            margin: { left: margin },
            headStyles: {
              fillColor: [0, 112, 192],   
              textColor: [255, 255, 255],
            },
          });
          currentY = doc.autoTable.previous.finalY + 7;
        }
      
        if (apiDetail.responseSample.length > 0) {
          addTitleWithLine("Response Samples:", margin, currentY, 12);
          apiDetail.responseSample.forEach((sample, idx) => {
            addParagraphText(`Code: ${sample.code || ''} - ${sample.description || ''}`, margin, currentY);
            addParagraphText(JSON.stringify(sample.json, null, 2), margin, currentY);
          });
        }
      });
      
    });
  
    addTitleWithLine(data.glossary.title, margin, currentY + 10);
    doc.autoTable({
      startY: currentY + 2,
      head: [['Name', 'Explanation']],
      body: data.glossary.codeData.map(item => [
        item.name || '',
        item.explanation || ''
      ]),
      margin: { left: margin },
     
      headStyles: {
        fillColor: [0, 112, 192],   
        textColor: [255, 255, 255],
      },
    
    });
    
    doc.save('API_Documentation.pdf');
  };
 

  React.useEffect(() => {
    if (selectedSubCategory) {
      setShowCategoryOverview(false);
    }
  }, [selectedSubCategory]);
  const isTablet = useMediaQuery("(max-width:899px)");

  const httpStatusCodeTableColumns = [
    { header: "Code", field: "code" },
    { header: "Name", field: "name" },
    { header: "Explanation", field: "explanation" },
  ];

  const httpStatusCodeTableRows = data?.httpStatusCode?.codeData?.map(
    (item) => ({
      code: item.code,
      name: item.name,
      explanation: item.explanation,
    })
  );

  const glossaryColumns = [
    { header: "Name", field: "name" },
    { header: "Explanation", field: "explanation" },
  ];

  const glossaryRows = data?.glossary?.codeData?.map((item) => ({
    name: item.name,
    explanation: item?.explanation,
  }));

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getApiDocsData();
        setData(res?.data?.data?.attributes);

        // Check if we have navigation state from OnscreenApi
        if (location.state?.selectedCategory && location.state?.selectedSubCategory) {
          setSelectedCategory(location.state.selectedCategory);
          setSelectedSubCategory(location.state.selectedSubCategory);
          setShowBackButton(location.state.showBackButton);
          setSelectedTabValue(`${location.state.selectedCategory}-${location.state.selectedSubCategory}`);
          // Scroll to API services section
          setTimeout(() => {
            scrollToPosition(apiServicesRef);
          }, 500);
        } else if (res?.data?.data?.attributes?.apiServices?.length > 0) {
          const firstCategory = res.data.data.attributes.apiServices[0];
          setSelectedCategory(firstCategory.categoryName);
          if (firstCategory.apiDetails.length > 0) {
            setSelectedSubCategory(firstCategory.apiDetails[0].APIDetail.title);
          }
        }
      } catch (error) {
        console.error("Error fetching API data:", error);
      }
    };
    fetchData();
  }, [location.state]);

  const apiServicesData = data?.apiServices?.map((service) => ({
    categoryName: service?.categoryName,
    categoryDescription: service?.description,
    commonUseCase: service?.commonUseCase,
    apiDetails: service?.apiDetails?.map((detail) => ({
      category: service?.categoryName,
      title: detail?.APIDetail?.title,
      description: detail?.APIDetail?.description,
      endpoint: detail?.endpoint,
      method: detail?.apiMethod,
      headerTitle: detail?.header?.title,
      headerDescription: detail?.header?.description,
      headerValue: detail?.header?.codeData?.map((headerItem) => ({
        name: headerItem?.name,
        explanation: headerItem?.explanation,
      })),
      requestParameterTitle: detail?.requestParameter?.title,
      requestParameterDescription: detail?.requestParameter?.description,
      requestParameterValue: detail?.requestParameter?.codeData?.map(
        (paramItem) => ({
          name: paramItem?.name,
          explanation: paramItem?.explanation,
        })
      ),
      curlCommand: detail?.curlCommand,
      requestSample: detail?.requestSample,
      responseSample: detail?.responseSample?.map((responseItem) => ({
        code: responseItem?.code,
        description: responseItem?.description,
        json: responseItem?.json,
      })),
    })),
  }));

  const introductionRef = React.useRef(null);
  const authenticationRef = React.useRef(null);
  const httpStatusCodeRef = React.useRef(null);
  const rateLimitRef = React.useRef(null);
  const apiServicesRef = React.useRef(null);
  const apiDocumentIdentification = React.useRef(null);
  const glossaryRef = React.useRef(null);

  const scrollToPosition = (ref) => {
    if (ref?.current) {
      const { top } = ref.current.getBoundingClientRect();
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const targetOffset = isTablet
        ? top + scrollTop - 100
        : top + scrollTop - 100;
      window.scrollTo({ top: targetOffset, behavior: "smooth" });
    }
  };

  const handleDrawerItemClick = (item) => {
    setSelectedTabValue(item.title);
    // For parent categories with children, don't set selectedDrawerItem
    if (!dropdownOptions.some(opt => opt.value === item.title)) {
      console.log("Clicked on item:", item.title);
      setSelectedDrawerItem(item.title);
    }
    // Close category overview when navigating to other sections
    if (item.title !== "Fintech API Services" && !dropdownOptions.some(opt => opt.value === item.title)) {
      setShowCategoryOverview(false);
      setSelectedCategory(null);
      setSelectedSubCategory(null);
    }
    switch (item.title) {
      case "Introduction":
        scrollToPosition(introductionRef);
        break;
      case "Authentication":
        scrollToPosition(authenticationRef);
        break;
      case "HTTP Status Codes":
        scrollToPosition(httpStatusCodeRef);
        break;
      case "Rate Limit":
        scrollToPosition(rateLimitRef);
        break;
      case "Fintech API Services":
        scrollToPosition(apiServicesRef);
        break;
      case "Document Identification":
        scrollToPosition(apiDocumentIdentification);
        break;
      case "Glossary":
        scrollToPosition(glossaryRef);
        break;
      default:
        break;
    }
  };

  const dropdownOptions =
    data?.apiServices?.map((item) => ({
      label: item.categoryName,
      value: item.categoryName,
      subCategories: item.apiDetails.map((detail) => detail.APIDetail.title),
    })) || [];

  return (
    <Box
      sx={{
        display: "flex",
        background: "#ffffff",
        flexDirection: {
          xs: "column",
          sm: "row",
         
        },
        width: {
          xs: "95%",  // phones
          sm: "90%",  // tablets (iPad portrait)
          md: "100%",  // iPad landscape / small laptop
          lg: "100%",  // desktops
          xl: "100%",  // very large screens
        },
        minHeight: "100vh",
      }}
    >
      <Box
        sx={{
          width: {
            xs: "100%",
            sm: "20%",
          },
          borderRight: 1,
          borderColor: "divider",
          paddingTop: { xs: "0px", md: "10px", lg: "70px" },
          backgroundColor: "#fff",
          minHeight: "100vh",
        }}
      >
        {console.log("Selected Category:", selectedCategory)}
        <Tabs
          orientation="vertical"
          // value={selectedDrawerItem || (showCategoryOverview ? selectedCategory : null)}
          value={selectedTabValue}
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
          sx={{
            borderRight: 1,
            borderColor: "divider",
            width: "100%",
            "& .MuiTab-root": {
              alignItems: "flex-start",
              justifyContent: "space-between",
              textAlign: "left",
              paddingX: 2,
              minHeight: "48px",
              fontSize: "16px",
              color: "#020817",
              textTransform: "none",
              borderRadius: 0,
              opacity: 1,
              "&:hover": {
                backgroundColor: "#F4F5F7 !important",
              },
            },
            "& .Mui-selected": {
              backgroundColor: "#E8E9F2",
              color: "#000",
              fontWeight: 500,
              opacity: 1,
            },
            "& .MuiTabs-indicator": {
              display: "none",
            },
            "& .selected-indicator": {
              display: "flex",
              backgroundColor: "#E8E9F2",
            },
          }}
        >
          {menu.map((item, index) =>
            hasChildren(item) ? (
              <React.Fragment key={index}>
                <Tab
                  label={item.title}
                  value={item.title}
                  // No icon for main tab
                />
                {dropdownOptions.map((opt, i) => (
                  <React.Fragment key={i}>
                    <Tab
                      label={`${opt.label}`}
                      value={opt.value}
                      onClick={() => handleCategoryClick(opt.value)}
                      icon={
                        selectedCategory === opt.value ? (
                          <KeyboardArrowDownIcon />
                        ) : (
                          <KeyboardArrowRightIcon />
                        )
                      }
                      iconPosition="end"
                      sx={{
                        pl: 3,
                        fontSize: "13px",
                        "&.Mui-selected": {
                          backgroundColor: "#E8E9F2 !important",
                          color: "#000 !important",
                          fontWeight: 500,
                        },
                      }}
                    />
                    {/* {selectedCategory === opt.value &&
                      opt.subCategories.map((sub, j) => (
                        <Tab
                          key={j}
                          label={`${sub}`}
                          value={`${opt.value}-${sub}`}
                          onClick={() => {
                            setSelectedSubCategory(sub);
                            setTimeout(() => {
                              scrollToPosition(apiServicesRef);
                            }, 100);
                          }}
                          sx={{ pl: 5, fontSize: "13px" }}
                        />
                      ))} */}
                    {selectedCategory === opt.value &&
                      opt.subCategories.map((sub, j) => {
                        const subValue = `${opt.value}-${sub}`; // ✅ define here

                        return (
                          <Tab
                            key={j}
                            label={`${sub}`}
                            value={subValue}
                            className={
                              selectedTabValue === subValue
                                ? "selected-indicator"
                                : ""
                            }
                            onClick={() => {
                              setSelectedSubCategory(sub);
                              setSelectedTabValue(subValue);
                              setTimeout(() => {
                                scrollToPosition(apiServicesRef);
                              }, 100);
                            }}
                            sx={{ pl: 5, fontSize: "13px" }}
                          />
                        );
                      })}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ) : (
              <Tab
                key={index}
                label={item.title}
                value={item.title}
                onClick={() => handleDrawerItemClick({ title: item.title })}
              />
            )
          )}
        </Tabs>
      </Box>

      <Box
        sx={{
          width: {
            xs: "100%",
            sm: "80%",
          },
          flexGrow: 1,
          marginTop: "100px",
          padding: 2,
        }}
      >
        <Box
          sx={{ display: "flex", justifyContent: "end", paddingRight: "15px" }}
        >
          <Button onClick={createApiDocumentPdf} variant="contained">
            Download PDF
          </Button>
        </Box>

        <div id="api-document-content">
          {showCategoryOverview ? (
            <CategoryOverview
              categoryData={data?.apiServices?.find(
                (service) => service.categoryName === selectedCategory
              )}
              onApiClick={handleApiClickFromOverview}
            />
          ) : (
            <>
              <DescriptionBox
                title={data?.Introduction?.title}
                refProp={introductionRef}
                description={data?.Introduction?.description}
              />
              <DescriptionBox
                title={data?.Authentication?.title}
                refProp={authenticationRef}
                description={data?.Authentication?.description}
              />
              <DescriptionBox
                title={data?.httpStatusCode?.title}
                refProp={httpStatusCodeRef}
                description={data?.httpStatusCode?.description}
                showBasicTable
                columns={httpStatusCodeTableColumns}
                rows={httpStatusCodeTableRows}
              />
              <DescriptionBox
                title={data?.rateLimit?.title}
                refProp={rateLimitRef}
                description={data?.rateLimit?.description}
              />
              <APIServices
                refProp={apiServicesRef}
                apiServicesData={apiServicesData}
                selectedCategory={selectedCategory}
                selectedSubCategory={selectedSubCategory}
                showBackButton={showBackButton}
                openAll={false}
              />
              <DescriptionBox
                title={data?.glossary?.title}
                refProp={glossaryRef}
                description={data?.glossary?.description}
                showBasicTable
                columns={glossaryColumns}
                rows={glossaryRows}
              />
              {/* <DocumentIdentification refProp={apiDocumentIdentification} /> */}
            </>
          )}
        </div>
      </Box>
    </Box>
  );
};
